import { useCallback, useEffect, useMemo, useState } from 'react';
import { readSignatureRecord } from '../../services/documentSignatures';

// Signed/lock status for the note at `documentPath`, read from the
// document_signatures table (one row per document). `currentUserEmail` is
// compared to the row's signer_email to decide `isSigner` — only the signer
// gets the password back and the reveal affordance.
//
// Returns { signed, signedBy, signedAt, signatureText, isSigner, password,
//           verifyPassword, loading, refresh }.
const EMPTY = { row: null };

export function useSignedStatus(documentPath, currentUserEmail = '', schema = null) {
  const [state, setState] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!documentPath) { setState(EMPTY); return; }
    setLoading(true);
    try {
      setState({ row: await readSignatureRecord(documentPath, schema) });
    } finally {
      setLoading(false);
    }
  }, [documentPath, schema]);

  useEffect(() => { refresh(); }, [refresh]);

  const row = state.row;
  const isSigner = !!row && !!currentUserEmail &&
    (row.signer_email || '').toLowerCase() === currentUserEmail.toLowerCase();

  const verifyPassword = useCallback(
    (input) => !!row && input != null &&
      String(input).trim() === String(row.lock_password || '').trim(),
    [row],
  );

  return useMemo(() => ({
    signed: !!row,
    signedBy: row ? (row.signer_name || row.signer_email || 'Unknown') : '',
    signedAt: row ? (row.signed_at || null) : null,
    signatureText: row ? (row.signature_text || '') : '',
    isSigner,
    password: isSigner ? (row.lock_password || '') : '',
    verifyPassword,
    loading,
    refresh,
  }), [row, isSigner, verifyPassword, loading, refresh]);
}
